export interface ProductImage {
  id: string;
  image: string;
  alt_text: string;
  order: number;
}

export interface RefurbishedInfo {
  grade: 'A' | 'B' | 'C';
  condition: string;
  warrantyMonths: number;
  includesAccessories: boolean;
}

export interface PssOptions {
  available: boolean;
  as_low_as_daily?: number;
  as_low_as_weekly?: number;
  as_low_as_monthly?: number;
  popular_plan_months?: number;
}

export class Product {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly search_name: string,
    public readonly brand_name: string,
    public readonly search_brand_name: string,
    public readonly model_no: string,
    public readonly description: string,
    public readonly rich_description: string,
    public readonly cost_price: number,
    public readonly retail_price: number,
    public readonly max_payment_period: number,
    public readonly images: ProductImage[],
    public readonly image_url: string | null,
    public readonly categories: string[],
    public readonly merchant_shop_id: string,
    public readonly shop_name: string,
    public readonly shop_logo: string,
    public readonly lender_id: string,
    public readonly is_featured: boolean,
    public readonly status: ProductStatus,
    public readonly postgres_id: string,
    public readonly created_time: Date,
    // New fields based on API documentation
    public readonly tags: string[] = [],
    public readonly pricing: ProductPricing,
    public readonly rating_average?: number,
    public readonly compare_at_price?: number,
    public readonly refurbished_info?: RefurbishedInfo,
    public readonly pss_options?: PssOptions,
    public readonly currency: string = 'GHS'
  ) {}

  /**
   * Create a Product from a plain object that has the same fields as the Product class.
   * Useful for cloning or constructing from deserialized data already normalized to domain fields.
   */
  static fromObject(obj: {
    id: string;
    name: string;
    search_name: string;
    brand_name: string;
    search_brand_name: string;
    model_no: string;
    description: string;
    rich_description: string;
    cost_price: number;
    retail_price: number;
    max_payment_period: number;
    images: ProductImage[];
    image_url: string | null;
    categories: string[];
    merchant_shop_id: string;
    shop_name: string;
    shop_logo: string;
    lender_id: string;
    is_featured: boolean;
    status: ProductStatus;
    postgres_id: string;
    created_time: Date;
    tags?: string[];
    pricing: ProductPricing;
    rating_average?: number;
    compare_at_price?: number;
    refurbished_info?: RefurbishedInfo;
    pss_options?: PssOptions;
    currency?: string;
  }): Product {
    return new Product(
      obj.id,
      obj.name,
      obj.search_name,
      obj.brand_name,
      obj.search_brand_name,
      obj.model_no,
      obj.description,
      obj.rich_description,
      obj.cost_price,
      obj.retail_price,
      obj.max_payment_period,
      obj.images,
      obj.image_url,
      obj.categories,
      obj.merchant_shop_id,
      obj.shop_name,
      obj.shop_logo,
      obj.lender_id,
      obj.is_featured,
      obj.status,
      obj.postgres_id,
      obj.created_time,
      obj.tags ?? [],
      obj.pricing,
      obj.rating_average,
      obj.compare_at_price,
      obj.refurbished_info,
      obj.pss_options,
      obj.currency ?? 'GHS'
    );
  }

  calculateDiscountPercentage(): number {
    const originalPrice = this.compare_at_price || this.retail_price;
    const currentPrice = this.retail_price;
    if (currentPrice >= originalPrice) return 0;
    return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
  }

  getDiscountAmount(): number {
    const originalPrice = this.compare_at_price || this.retail_price;
    const currentPrice = this.retail_price;
    return Math.max(0, originalPrice - currentPrice);
  }

  isDiscounted(): boolean {
    return this.calculateDiscountPercentage() > 0;
  }

  isRefurbished(): boolean {
    return !!this.refurbished_info;
  }

  getPssMonthlyPayment(months: number): number {
    if (!this.isEligibleForPaymentPlan() || months <= 0) return 0;
    return this.retail_price / months;
  }

  getPssWeeklyPayment(weeks: number): number {
    if (!this.isEligibleForPaymentPlan() || weeks <= 0) return 0;
    return this.retail_price / weeks;
  }

  isAvailable(): boolean {
    return this.status === ProductStatus.ACTIVE;
  }

  isEligibleForPaymentPlan(): boolean {
    return this.max_payment_period > 0 && this.isAvailable();
  }

  getFormattedPrice(): string {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
    }).format(this.retail_price);
  }
}

export enum ProductStatus {
  ACTIVE = 'A',
  INACTIVE = 'I',
  OUT_OF_STOCK = 'O',
  DISCONTINUED = 'D',
}

export interface ProductCreateParams {
  id: string;
  name: string;
  brand_name: string;
  model_no: string;
  cost_price: number;
  retail_price: number;
  max_payment_period: number;
  images: ProductImage[];
  categories: string[];
  merchant_shop_id: string;
  shop_name: string;
  shop_logo: string;
  lender_id: string;
  postgres_id: string;
  search_name?: string;
  search_brand_name?: string;
  description?: string;
  rich_description?: string;
  is_featured?: boolean;
  status?: ProductStatus;
  created_time?: Date;
  tags?: string[];
  rating_average?: number;
  compare_at_price?: number;
  refurbished_info?: RefurbishedInfo;
  pss_options?: PssOptions;
  currency?: string;
}

export interface ProductUpdateParams
  extends Partial<Omit<ProductCreateParams, 'id' | 'postgres_id'>> {}

export class Category {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly banner_url: string,
    public readonly active: boolean,
    public readonly slug: string,
    public readonly parent_id?: string,
    public readonly icon_url?: string,
    public readonly product_count?: number,
    public readonly sort_order: number = 0,
    public readonly is_featured: boolean = false,
    public readonly is_featured_menu: boolean = false,
    public readonly children: Category[] = []
  ) {}

  static create(params: CategoryCreateParams): Category {
    return new Category(
      params.id,
      params.name,
      params.banner_url,
      params.active ?? true,
      params.slug || params.name.toLowerCase().replace(/\s+/g, '-'),
      params.parent_id,
      params.icon_url,
      params.product_count,
      params.sort_order || 0,
      params.is_featured || false,
      params.is_featured_menu || false
    );
  }

  isActive(): boolean {
    return this.active;
  }
}

export interface CategoryCreateParams {
  id: string;
  name: string;
  banner_url: string;
  active?: boolean;
  slug?: string;
  parent_id?: string;
  icon_url?: string;
  product_count?: number;
  sort_order?: number;
  is_featured?: boolean;
  is_featured_menu?: boolean;
}

export class Store {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly logo: string,
    public readonly image: string,
    public readonly category: string
  ) {}

  static create(params: StoreCreateParams): Store {
    return new Store(
      params.id,
      params.name,
      params.logo,
      params.image,
      params.category
    );
  }
}

export interface StoreCreateParams {
  id: string;
  name: string;
  logo: string;
  image: string;
  category: string;
}

export interface HomepageSection {
  key: string;
  title: string;
  description: string;
  products: Product[];
}

export interface HomepageCollections {
  sections: HomepageSection[];
}

export interface ProductCollection {
  id: string;
  name: string;
  description?: string;
  products: Product[];
  filters?: ProductFilters;
}

export interface ProductFilters {
  categories?: string[];
  brandName?: string;
  minPrice?: number;
  maxPrice?: number;
  isFeatured?: boolean;
  status?: string;
  limit?: number;
  offset?: number;
  sortBy?: 'price' | 'name' | 'created' | 'featured';
  sortOrder?: 'asc' | 'desc';
  tags?: string[];
  refurbished?: boolean;
  grade?: 'A' | 'B' | 'C';
  warrantyMonths?: number;
}

export interface ProductSearchFilters extends ProductFilters {
  query: string;
  filtersApplied?: ProductFilters;
  sortApplied?: string;
}

export interface PaginatedResponse<T> {
  results: T[];
  count: number;
  next?: string;
  previous?: string;
}

export interface ProductApiResponse extends PaginatedResponse<Product> {
  filtersApplied?: ProductFilters;
  sortApplied?: string;
}

// API Response Types based on documentation
export interface ApiProductResponse {
  id: string;
  name: string;
  image_url?: string;
  pricing: {
    current_display_price?: string;
    display_price: string;
    currency: string;
    original_price?: string;
    compare_at_price?: string;
    is_discounted?: boolean;
    discount?: {
      amount: string;
      percent: number;
    };
  };
  pssOptions?: {
    available: boolean;
    as_low_as_daily?: number;
    as_low_as_weekly?: number;
    as_low_as_monthly?: number;
    popular_plan_months?: number;
  };
  stock_status: string;
  rating_average?: number;
  category?: string;
  brand?: string;
  tags?: string[];
  refurbished_info?: {
    grade: 'A' | 'B' | 'C';
    condition: string;
    warranty_months: number;
    includes_accessories: boolean;
  };
  is_featured?: boolean;
  created_at?: string;
}

export interface ApiCategoryResponse {
  id: string;
  slug: string;
  name: string;
  parent_id?: string;
  icon_url?: string;
  banner_image_url?: string;
  product_count?: number;
  sort_order?: number;
  is_featured?: boolean;
  is_featured_menu?: boolean;
  children?: ApiCategoryResponse[];
}

export interface ApiHomepageSection {
  key: string;
  title: string;
  description: string;
  results: ApiProductResponse[];
}

export interface ApiHomepageCollections {
  sections: ApiHomepageSection[];
}

export interface ApiPaginatedResponse<T> {
  count: number;
  next?: string;
  previous?: string;
  results: T[];
}

export interface ApiProductsResponse
  extends ApiPaginatedResponse<ApiProductResponse> {
  filtersApplied?: ProductFilters;
  sortApplied?: string;
}

export interface ApiCategoriesResponse
  extends ApiPaginatedResponse<ApiCategoryResponse> {}

export interface ApiFiltersResponse {
  categories?: Array<{
    id: string;
    name: string;
    count: number;
  }>;
  brands?: Array<{
    count: number;
    name: string;
    slug: string;
  }>;
  price_range?: Array<{
    min: number;
    max: number;
    count: number;
  }>;
  tags?: Array<{
    name: string;
    count: number;
  }>;
  vendor_assignment?: Array<{
    name: string;
    count: number;
  }>;
}

interface ProductPricing {
  display_price: string;
  current_display_price: string;
  currency: string;
  original_price?: string;
  is_discounted: boolean;
  discount?: {
    amount: string;
    percent: number;
  };
}

// Payment options interfaces (derived from sample JSON)
export interface ProductPaymentOptionsResponse {
  product_id: string;
  currency: string;
  payment_options: PaymentOption[];
}
export interface ProductPaymentPlanResponse {
  product: {
    image_url?: string;
    id: string;
    name: string;
    pricing: {
      price: string;
      currency: string;
    };
  };
  kmp_policy: {
    available: boolean;
    base_percentage: number;
    min_fee: string;
    currency: string;
  };
  user_eligibility: {
    rep_score: number;
    pss_credit_eligible: boolean;
    pss_credit_percentage: number;
    early_redemption_threshold: number;
    recommended_duration: number;
    max_financeable_amount: string;
  };
  duration_options: DurationOption[];
}

export interface DurationOption {
  months: number;
  daily: PaymentFrequency;
  weekly: PaymentFrequency;
  monthly: PaymentFrequency;
}

export interface PaymentFrequency {
  without_kmp: PaymentPlanDetails;
  with_kmp: PaymentPlanDetailsWithKmp;
  schedule?: PaymentSchedule;
}

export interface PaymentPlanDetails {
  period_amount: string;
  total_amount: string;
  installment_count: number;
}

export interface PaymentPlanDetailsWithKmp extends PaymentPlanDetails {
  kmp_fee: string;
  kmp_fee_percentage: number;
}

export interface PaymentSchedule {
  first_due_date: string;
  completion_date: string;
}

export interface PaymentMethod {
  type: 'telecel_mobile_money' | 'mtn_mobile_money' | 'debit_card';
  provider: 'telecel_momo' | 'mtn_momo' | 'paystack_card';
}

export interface PaymentMethodGroups {
  primary: PaymentMethod[];
}

export interface PssCreditEligibility {
  eligible: boolean;
  requires_auth: boolean;
  rep_score: number | null;
  pss_credit_percentage: number | null;
  max_financeable_amount: number | null;
  min_rep_score_required: number | null;
}

export interface PlanPreview {
  tenor_months: number;
  frequency: 'monthly' | string;
  installment_count: number;
  installment_amount: string;
  downpayment_amount: string;
  kmp_fee: string;
  kmp_fee_percentage: number;
  total_payable: string;
  as_low_as_monthly: number;
  as_low_as_weekly: number;
  currency: string;
  early_redemption_eligible: boolean;
}

interface BasePaymentOption {
  type: 'pay_in_full' | 'pay_small_small' | string;
  available: boolean;
  requires_login: boolean;
  payment_methods: PaymentMethodGroups;
  currency: string;
}

export interface PayInFullOption extends BasePaymentOption {
  type: 'pay_in_full';
}

export interface PaySmallSmallOption extends BasePaymentOption {
  type: 'pay_small_small';
  provider: string;
  restricted_payment_method: string;
  pss_credit_eligibility: PssCreditEligibility;
  plan_preview: PlanPreview;
  labels?: string[];
}

export type PaymentOption = PayInFullOption | PaySmallSmallOption;
